<template>
    <div>
        <Toast />
        <InputText v-model="searchQuery" placeholder="Search advertisers..."
                    class="w-full pl-10 transition-all duration-300" :class="{ 'search-highlight': searchHighlight }"
                    ref="searchInput" />
        <div class="flex items-end gap-3 flex-wrap mt-3">
            <div class="flex flex-col gap-1">
                <label class="text-xs opacity-60">Publisher</label>
                <Select v-model="publisherFilter" :options="publisherOptions" optionLabel="label"
                    optionValue="value" placeholder="All publishers" showClear filter class="w-72" />
            </div>
            <div class="flex flex-col gap-1">
                <label class="text-xs opacity-60">Sort by</label>
                <Select v-model="sortBy" :options="sortOptions" optionLabel="label"
                    optionValue="value" class="w-56" />
            </div>
            <span class="text-xs opacity-60 pb-2 ml-auto">
                {{ filteredAdvertisers.length }} advertiser{{ filteredAdvertisers.length === 1 ? '' : 's' }}
                <template v-if="publisherFilter"> · {{ visibleLinkCount }} link{{ visibleLinkCount === 1 ? '' : 's' }}</template>
            </span>
        </div>
        <div class="flex justify-center items-center  pt-5">
            <div class="w-full rounded-lg shadow-md">
                <DataTable v-model:expandedRows="expandedRows"
                    :value="filteredAdvertisers" dataKey="advertiserId" scrollable :scrollHeight="'82vh'">
                    <Column expander style="width: 5rem" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column field="advertiserName" header="Name" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                    </Column>
                    <Column v-if="props.showing === 'requested'" field="requestedLinksCount" header="Requested Links" :headerStyle="headerStyles">
                    </Column>
                    <Column v-else field="linkedSites.length" header="Existing links" :headerStyle="headerStyles">
                    </Column>   
                    <Column v-if="props.showing !== 'requested'" header="Mapped" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            <span v-if="linkedCount(data) === 0" class="opacity-40">—</span>
                            <span v-else v-tooltip.left="mappingTooltip(data)"
                                :class="mappedCount(data) === linkedCount(data) ? 'text-green-500' : 'text-amber-500 font-semibold'">
                                {{ mappedCount(data) }} / {{ linkedCount(data) }}
                            </span>
                        </template>
                    </Column>
                    <Column field="markets" header="Markets" :headerStyle="headerStyles">
                        <template v-if="isLoading" #body>
                            <Skeleton></Skeleton>
                        </template>
                        <template v-else #body="{ data }">
                            <div v-tooltip.left="marketTooltip(data.markets)">
                                <span v-for="marketName in data.markets" :key="marketName"
                                    :class="marketClass(marketName)" class="ml-2">
                                </span>
                            </div>
                        </template>
                    </Column>
                    <template #expansion="slotProps">
                        <div v-if="anySitesByAdvertiserId(slotProps.data.advertiserId)" class="p-4">
                            <DataTable :value="getSitesByAdvertiserId(slotProps.data.advertiserId)"
                                dataKey="siteId" @row-click="handleRowClick">
                                <Column field="name" header="Site name" :headerStyle="nestedHeaderStyles">
                                    <template #body="{ data }">
                                        <div class="flex items-center gap-2">
                                            <a :href="$router.resolve({
                                                name: 'UserDetails',
                                                params: { id: data.userId },
                                                query: {
                                                    siteId: data.siteId,
                                                    linkId: data.linkId,
                                                    showing: data.status === 'requested' ? 'requested' : 'all'
                                                }
                                            }).href" target="_blank" class="link-class">
                                                {{ data.name }}
                                            </a>
                                            <div v-tooltip.left="data.market">
                                                <span :key="data.market" :class="marketClass(data.market)" class="ml-2">
                                                </span>
                                            </div>
                                        </div>
                                    </template></Column>
                                <Column field="userName" header="User" :headerStyle="nestedHeaderStyles">
                                    <template #body="slotProps">
                                        <!-- Create a router link using the data in the slotProps -->
                                        <router-link :to="`/users/${slotProps.data.userId}`" class="clickable-link">
                                            {{ slotProps.data.userType === 'Individual' ? slotProps.data.userName : slotProps.data.userCompanyName }}
                                        </router-link>
                                    </template>
                                </Column>
                                <Column field="deal" header="Pub Deal" :headerStyle="nestedHeaderStyles">
                                    <template #body="{ data }">
                                        <a v-if="data.affiliateSystemLoginUrl" 
                                            :href="data.affiliateSystemLoginUrl" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            class="link-class">
                                            {{ getFriendlyDealMessage(data.deal) }}
                                        </a>
                                        <span v-else>{{ getFriendlyDealMessage(data.deal) }}</span>
                                    </template>
                                </Column>
                                <Column field="ourDeal" header="Our Deal" :headerStyle="nestedHeaderStyles">
                                    <template #body="{ data }">
                                        <a v-if="data.affiliateSystemLoginUrl" 
                                            :href="data.affiliateSystemLoginUrl" 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            class="link-class">
                                            {{ getFriendlyDealMessage(data.ourDeal) }}
                                        </a>
                                        <span v-else>{{ getFriendlyDealMessage(data.ourDeal) }}</span>
                                    </template>
                                </Column>
                                <Column field="internalNote" header="Internal Note" :headerStyle="nestedHeaderStyles">
                                    <template #body="slotProps">
                                        <div v-if="editingLinkId === slotProps.data.linkId">
                                            <InputText v-model="slotProps.data.internalNote" class="w-full" />
                                        </div>
                                        <div v-else>
                                            {{ slotProps.data.internalNote }}
                                        </div>
                                    </template>
                                </Column>
                                <Column header="Campaign" :headerStyle="nestedHeaderStyles">
                                    <template #body="{ data }">
                                        <span v-if="data.status !== 'linked'" class="opacity-40"
                                            v-tooltip.left="'Not linked yet, so there is nothing to map'">—</span>
                                        <Tag v-else-if="data.mapped" severity="success"
                                            :value="data.campaignKey ? `Mapped · ${data.campaignKey}` : 'Mapped'" />
                                        <a v-else :href="mappingPageHref(data)" target="_blank" rel="noopener noreferrer"
                                            v-tooltip.left="'No Voonix campaign is mapped, so no earnings reach this link. Opens the mapping page.'">
                                            <Tag severity="warn" value="Not mapped" icon="pi pi-external-link" />
                                        </a>
                                    </template>
                                </Column>
                                <Column field="affiliateLink" header="Affiliate Link" :headerStyle="nestedHeaderStyles"
                                    style="min-width: 30rem;">
                                    <template #body="{ data }">
                                        <div v-if="editingLinkId === data.linkId" class="w-full min-w-[26rem]">
                                            <InputText v-model="data.affiliateLink"
                                                class="w-full min-w-[26rem]"
                                                :class="{ 'p-invalid': !isValidAffiliateLink(data.affiliateLink, data.internalNote, data.affiliateSystem) && data.affiliateLink }"
                                                @input="handleAffiliateLinkInput(data, slotProps.data.advertiserName)"
                                                @keydown.enter="saveEdit(data)" />
                                            <small class="p-error flex items-center gap-2" v-if="!isValidAffiliateLink(data.affiliateLink, data.internalNote, data.affiliateSystem)">
                                                <span>{{ expectedAffiliateLinkFormat(data.affiliateSystem, data.internalNote) }}</span>
                                                <Button v-if="data.affiliateSystem && getLinkParamsText(data.affiliateSystem, data.internalNote)"
                                                    icon="pi pi-copy" 
                                                    severity="info"
                                                    class="p-button-text p-button-sm" 
                                                    v-tooltip.bottom="'Copy link parameters'"
                                                    @click="copyLinkParams(data.affiliateSystem, data.internalNote)" />
                                            </small>
                                        </div>
                                        <div v-else class="flex items-center gap-2">
                                            <span>{{ data.affiliateLink }}</span>
                                            <Button v-if="data.affiliateLink" 
                                                icon="pi pi-copy" 
                                                class="p-button-text p-button-sm" 
                                                v-tooltip.bottom="'Copy link parameters'"
                                                @click="copyLinkParams(data.affiliateSystem, data.internalNote)" />
                                        </div>
                                    </template>
                                </Column>
                                <Column field="createdAt" header="Created At" :headerStyle="nestedHeaderStyles">
                                    <template #body="slotProps">
                                        {{ slotProps.data.createdAt ? new Date(slotProps.data.createdAt).toLocaleDateString() : '-' }}
                                    </template>
                                </Column>
                                
                                <Column field="status" header="Link status" :headerStyle="nestedHeaderStyles">
                                    <template #body="slotProps">
                                        <div v-if="editingLinkId === slotProps.data.linkId">
                                            <Button icon="pi pi-check" class="p-button-text p-button-success"
                                                @click="saveEdit(slotProps.data)" />
                                            <Button icon="pi pi-times" class="p-button-text p-button-danger"
                                                @click="cancelEdit()" />
                                        </div>
                                        <div v-else>
                                            <Button icon="pi pi-question-circle"
                                                class="p-button-text p-button-help"
                                                v-tooltip.left="helpButtonTooltip(slotProps.data.affiliateSystem)"
                                                @click="openHelpForSystem(slotProps.data.affiliateSystem)" />
                                            <Button icon="pi pi-pencil" class="p-button-text p-button-info"
                                                @click="editRow(slotProps.data.linkId)" />
                                            <Button icon="pi pi-trash" class="p-button-text p-button-danger"
                                                @click="softDeleteSiteBrandLink(slotProps.data, slotProps.index)" />
                                        </div>
                                        <Tag v-if="props.showing !== 'requested'" :value="slotProps.data.status"
                                            :severity="Utils.getLinkStatusSeverity(slotProps.data.status)" />
                                    </template>
                                </Column>
                               
                            </DataTable>
                        </div>
                        <div class="flex items-center justify-center h-full" v-else>
                            <h5> Linked sites will appear here</h5>
                        </div>
                    </template>
                </DataTable>
            </div>
        </div>
        <DuplicateCampaignKeyDialog v-model:visible="showDuplicateKeyDialog" :details="duplicateKeyDetails" />
    </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, watch, onUnmounted, h } from "vue";
import { useRouter } from 'vue-router';
import Utils from '@/utils';
import { isValidAffiliateLink, expectedAffiliateLinkFormat } from '@/utils/tracking-link-validator';
import { enrichAffiliateLink } from '@/utils/affiliate-link-enricher';
import { helpButtonTooltip, helpQueryForSystem, getLinkParamsText, isCampaignKeyTaken } from '@/utils/affiliate-system-help';
import DuplicateCampaignKeyDialog from '@/components/DuplicateCampaignKeyDialog.vue';
import { useToast } from "primevue/usetoast";
import { headerStyles, nestedHeaderStyles } from '@/global/headerStyles';
import AdvertiserService from "@/services/AdvertiserService";
import { marketClass, getCountryCode, marketTooltip, getDistinctMarketsByName, getDistinctMarkets } from '@/utils/markets';
import { getFriendlyDealMessage } from '@/utils/deals';
import SiteLinkService from "@/services/SiteLinkService";
import Button from 'primevue/button';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
const toast = useToast();
const router = useRouter();

function openHelpForSystem(system) {
    const url = router.resolve({ name: 'Help', query: helpQueryForSystem(system) }).href;
    window.open(url, '_blank', 'noopener,noreferrer');
}

// Shown when a save is refused because the campaign key is taken on this login.
const showDuplicateKeyDialog = ref(false);
const duplicateKeyDetails = ref(null);

const props = defineProps({
    showing: String // Accept 'showing' as a prop, which can be, requested or all
});

const isLoading = ref(true);
const advertisers = ref([]);
const expandedRows = ref({});
const searchQuery = ref('');
const searchInput = ref(null);
const searchHighlight = ref(false);
const editingLinkId = ref(null);

// --- publisher filter + sort ---------------------------------------------
const publisherFilter = ref(null);
const sortBy = ref('name');

const sortOptions = [
    { label: 'Name (A\u2013Z)', value: 'name' },
    { label: 'Last added', value: 'lastAdded' },
    { label: 'Most links', value: 'links' },
];

// Publishers are only present on the nested links, so the dropdown is derived
// from them rather than fetched. Deduped by userId; an individual shows their
// name, a company its company name, matching the nested User column.
const publisherOptions = computed(() => {
    const byId = new Map();
    for (const advertiser of advertisers.value) {
        for (const link of advertiser?.linkedSites || []) {
            if (!link?.userId || byId.has(link.userId)) continue;
            const label = link.userType === 'Individual'
                ? (link.userName || link.userCompanyName)
                : (link.userCompanyName || link.userName);
            if (label) byId.set(link.userId, { label, value: link.userId });
        }
    }
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label));
});

// An advertiser's "last added" is the newest createdAt across its links, so a
// new link under an old advertiser lifts it to the top. Advertisers with no
// dated links sort last rather than pretending to be ancient.
function lastAddedAt(advertiser) {
    let newest = 0;
    for (const link of advertiser?.linkedSites || []) {
        const t = link?.createdAt ? new Date(link.createdAt).getTime() : 0;
        if (Number.isFinite(t) && t > newest) newest = t;
    }
    return newest;
}

// Links visible under the current publisher filter — drives both the nested
// tables and the count in the toolbar, so the two can't disagree.
function linksFor(advertiser) {
    const links = Array.isArray(advertiser?.linkedSites) ? advertiser.linkedSites : [];
    const byStatus = props.showing === 'requested'
        ? links.filter(l => l.status === 'requested')
        : links;
    return publisherFilter.value
        ? byStatus.filter(l => l.userId === publisherFilter.value)
        : byStatus;
}

const visibleLinkCount = computed(() =>
    filteredAdvertisers.value.reduce((n, a) => n + linksFor(a).length, 0));

const filteredAdvertisers = computed(() => {
    let toRet = advertisers.value;
    if (searchQuery.value) {
        toRet = toRet.filter(advertiser => {
            return advertiser.advertiserName.toLowerCase().includes(searchQuery.value.toLowerCase());
        });
    }

    // Narrow to advertisers this publisher actually has links with, and rebuild
    // markets from just those links. New objects only — mutating
    // advertiser.markets here is what previously made this computed invalidate
    // its own dependency and blow up the DataTable.
    if (publisherFilter.value) {
        toRet = toRet
            .filter(a => (a.linkedSites || []).some(l => l.userId === publisherFilter.value))
            .map(a => ({
                ...a,
                markets: [...new Set(
                    a.linkedSites
                        .filter(l => l.userId === publisherFilter.value)
                        .map(l => l.market)
                        .filter(Boolean)
                )],
            }));
    }

    if (props.showing === 'requested') {
        if (advertisers.value.length === 0) return [];

        // Filter to advertisers that have at least one requested link, and
        // return NEW objects with markets narrowed to just the requested-link
        // markets. We deliberately don't mutate the source `advertiser.markets`
        // — that used to happen here and made this computed invalidate its
        // own reactive dependency, blowing the DataTable up with
        // "Maximum recursive updates exceeded" once the requested filter
        // returned any rows.
        toRet = toRet
            .filter(advertiser => Array.isArray(advertiser.linkedSites)
                && advertiser.linkedSites.some(site => site.status === 'requested'))
            .map(advertiser => ({
                ...advertiser,
                markets: [...new Set(
                    advertiser.linkedSites
                        .filter(site => site.status === 'requested')
                        .map(site => site.market)
                        .filter(Boolean)
                )],
            }));
    }

    // Sort last, so it applies to every path above rather than only the
    // unfiltered one. Copy before sorting — Array.prototype.sort mutates, and
    // sorting advertisers.value in place would write to the source this
    // computed reads from.
    return sortAdvertisers([...toRet]);
});

function sortAdvertisers(list) {
    if (sortBy.value === 'lastAdded') {
        // Newest first. Advertisers with no dated link (0) fall to the bottom
        // instead of masquerading as the oldest.
        return list.sort((a, b) => lastAddedAt(b) - lastAddedAt(a));
    }
    if (sortBy.value === 'links') {
        return list.sort((a, b) => linksFor(b).length - linksFor(a).length
            || a.advertiserName.localeCompare(b.advertiserName));
    }
    return list.sort((a, b) => (a.advertiserName || '').localeCompare(b.advertiserName || ''));
}

// A per-advertiser cache of the derived linkedSites arrays, keyed by advertiserId.
// The cache is rebuilt whenever filteredAdvertisers or props.showing changes, so
// the template can call getSitesByAdvertiserId() unlimited times per render
// without producing a new array reference each call. Returning a fresh array
// from every call is what previously blew the DataTable up with "Maximum
// recursive updates exceeded" — the nested DataTable saw a new `:value` on
// every render, triggering another render, forever.
const sitesByAdvertiserIdCache = computed(() => {
    const map = new Map();
    for (const advertiser of filteredAdvertisers.value) {
        if (!Array.isArray(advertiser?.linkedSites)) {
            map.set(advertiser?.advertiserId, []);
            continue;
        }
        // linksFor() applies BOTH the requested-status narrowing and the
        // publisher filter, so a filtered nested table can never show links
        // belonging to a publisher the operator has filtered out.
        map.set(advertiser.advertiserId, linksFor(advertiser));
    }
    return map;
});

const getSitesByAdvertiserId = (advertiserId) => sitesByAdvertiserIdCache.value.get(advertiserId) || [];
const anySitesByAdvertiserId = (advertiserId) => getSitesByAdvertiserId(advertiserId).length > 0;

// Earnings only reach a link once a Voonix campaign is mapped to it. Counted over
// the links visible under the publisher filter, like the nested table; requested
// links aren't linked yet, so they don't count either way.
function linkedLinks(advertiser) {
    return linksFor(advertiser).filter(l => l.status === 'linked');
}
const linkedCount = (advertiser) => linkedLinks(advertiser).length;
const mappedCount = (advertiser) => linkedLinks(advertiser).filter(l => l.mapped).length;

function mappingTooltip(advertiser) {
    const missing = linkedCount(advertiser) - mappedCount(advertiser);
    if (missing === 0) return 'Every linked link has a campaign mapped';
    return `${missing} linked link${missing === 1 ? '' : 's'} without a campaign`;
}

// /campaigns opens straight onto the campaign whose key equals the link's internal
// note (the key automapping matches on), scoped to the link's Voonix advertiser.
// Without a note there's no key to open, so just search for the advertiser.
function mappingPageHref(link) {
    let query = {};
    if (link.internalNote) {
        query = { campaignKey: link.internalNote };
        if (link.voonixAdvertiserName) query.advertiser = link.voonixAdvertiserName;
    } else if (link.voonixAdvertiserName) {
        query = { q: link.voonixAdvertiserName };
    }
    return router.resolve({ name: 'Campaigns', query }).href;
}

const expandRowsForRequested = () => {
    if (props.showing === 'requested') {
        const expanded = {};
        filteredAdvertisers.value.forEach(advertiser => {
            expanded[advertiser.advertiserId] = true; // Assuming 'id' is the unique identifier
        });
        expandedRows.value = expanded;
    } else {
        expandedRows.value = {}; // Collapse all rows if not 'requested'
    }
};

async function fetchAdvertisersWithLinks() {
    try {
        const data = await AdvertiserService.getAllAdvertisersWithLinks();
        advertisers.value = Array.isArray(data?.data) ? data.data : [];
    } catch (err) {
        console.error(err);
        advertisers.value = [];
    }
}

function handleRowClick(event) {
    if (props.showing === 'requested' && event.data && event.data.linkId) {
        editRow(event.data.linkId);
    }
}

function editRow(linkId) {
    editingLinkId.value = linkId;
}

function cancelEdit() {
    editingLinkId.value = null;
}



async function saveEdit(data) {
    if (!isValidAffiliateLink(data.affiliateLink, data.internalNote, data.affiliateSystem)) {
        toast.add({ severity: 'error', summary: 'Validation failed', detail: 'Please enter a valid affiliate link.', life: 3000 });
        return;
    } else {
        console.log('VALID', data.affiliateLink, data.internalNote, data.affiliateSystem);
        const dataUpdate = {
            internalNote: data.internalNote,
            affiliateLink: data.affiliateLink
        };
        try{
            const response = await SiteLinkService.updateSiteBrandLink(data.linkId, dataUpdate);
            if (response.status === 200) {
                // Find the advertiser by data.advertiserId
                const updatedAdvertiser = advertisers.value.find(adv => adv.advertiserId === data.advertiserId);
                if (updatedAdvertiser && Array.isArray(updatedAdvertiser.linkedSites)) {
                    // Find the linkedSite by data.linkId
                    const updatedLinkedSite = updatedAdvertiser.linkedSites.find(site => site.linkId === data.linkId);
                    if (updatedLinkedSite) {
                        updatedLinkedSite.affiliateLink = response.data.affiliateLink;
                        updatedLinkedSite.internalNote = response.data.internalNote;
                        updatedLinkedSite.status = response.data.status;
                    }
                }
                toast.add({ severity: 'success', summary: 'Success', detail: 'Site brand link updated successfully', life: 3000 });
                editingLinkId.value = null;
            }
        } catch (err) {
            console.error(err);
            if (isCampaignKeyTaken(err)) {
                duplicateKeyDetails.value = err.response.data;
                showDuplicateKeyDialog.value = true;
                return;
            }
            const errorMessage = err.response?.data?.message || err.message || 'Failed to update site brand link';
            toast.add({ severity: 'error', summary: 'Error', detail: errorMessage, life: 3000 });
        }
    }
}


function handleAffiliateLinkInput(data, advertiserName) {
    const context = {
        advertiserName,
        userName: data.userName,
        userCompanyName: data.userCompanyName,
        userType: data.userType,
        market: data.market,
    };

    const result = enrichAffiliateLink(data.affiliateLink, data.affiliateSystem, context);
    if (!result) return;

    if (result.affiliateLink !== data.affiliateLink) {
        data.affiliateLink = result.affiliateLink;
    }
    if (result.internalNote && result.internalNote !== data.internalNote) {
        data.internalNote = result.internalNote;
    }
}

async function copyLinkParams(system, internalNote = '') {
    const textToCopy = getLinkParamsText(system, internalNote);
    if (!textToCopy) {
        toast.add({ severity: 'warn', summary: 'Warning', detail: 'No link parameters available for this system', life: 3000 });
        return;
    }
    try {
        await navigator.clipboard.writeText(textToCopy);
        toast.add({ severity: 'success', summary: 'Success', detail: 'Link parameters copied to clipboard', life: 3000 });
    } catch (err) {
        console.error(err);
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to copy to clipboard', life: 3000 });
    }
}


async function softDeleteSiteBrandLink(data, index) {
    try {
        const response = await SiteLinkService.softDeleteSiteBrandLink(data.linkId);
        if (response.status === 200) {
            toast.add({ severity: 'success', summary: 'Success', detail: 'Site brand link deleted successfully', life: 3000 });
            await fetchAdvertisersWithLinks();
        } else {
            toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete site brand link', life: 3000 });
        }
    } catch (err) {
        console.error(err);
        toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete site brand link', life: 3000 });
    }
}

onMounted(async () => {
    await Promise.all([
        fetchAdvertisersWithLinks()
    ]);
    //expandRowsForRequested();
    isLoading.value = false;
    document.addEventListener('keydown', handleKeyDown);
});

watch(() => props.showing, expandRowsForRequested);

function handleKeyDown(event) {
    // Check for Ctrl+F (or Cmd+F on Mac)
    if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        // Prevent the default browser search
        event.preventDefault();
        if (searchInput.value && searchInput.value.$el) {
            searchInput.value.$el.focus();
            searchHighlight.value = true;
            setTimeout(() => {
                searchHighlight.value = false;
            }, 1000);
        }
    }
}

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeyDown);
});

</script>

<style scoped>
.link-class {
    text-decoration: underline;
    cursor: pointer;
    color: var(--color-primary);
}

.link-class:hover {
    color: white;
}
</style>
